import { useMemo } from 'react';
import { Textarea } from '@mantine/core';
import './CrepeEditor.css';

export interface CrepeEditorProps {
  content: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  variant?: 'default' | 'compact';
  readOnly?: boolean;
  className?: string;
}

/**
 * Simple markdown editor using Textarea
 *
 * This is a simplified editor that focuses on:
 * - Reliable text input without complex editor framework issues
 * - GFM markdown support (through MilkdownRenderer preview)
 * - Split-view with live preview pattern
 * - No initialization errors or schema conflicts
 *
 * Variants:
 * - default: Full height
 * - compact: Reduced height
 */

export default function CrepeEditor({
  content,
  onChange,
  placeholder = 'Enter markdown...',
  variant = 'default',
  readOnly = false,
  className = '',
}: CrepeEditorProps) {
  const variantClass = useMemo(() => {
    switch (variant) {
      case 'compact':
        return 'crepe-compact';
      default:
        return 'crepe-default';
    }
  }, [variant]);

  return (
    <Textarea
      value={content}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      className={`crepe-editor ${variantClass} ${className}`}
      styles={{
        input: {
          fontFamily: 'monospace',
          fontSize: '0.9rem',
          height: '100%',
          minHeight: variant === 'compact' ? '200px' : '300px',
          resize: 'none',
          border: 'none',
        },
      }}
    />
  );
}
